const sessionOwner = user => `${user?.id || user?._id || user?.email || ''}`;

// Keep request ownership across token rotations, but invalidate it on login/logout.
export function installSessionAuth({ client, userStore, refreshSession, clearSession }) {
    let generation = 0;
    let pendingRefresh = null;
    let refreshCommit = null;

    const unsubscribe = userStore.$onAction(({ name, args }) => {
        if (!['setSession', 'clearSession', 'hydrateFromStorage'].includes(name)) return;
        if (name === 'setSession' && args[0] === refreshCommit) return;
        generation += 1;
        pendingRefresh = null;
    }, true);

    const snapshot = () => ({
        generation,
        owner: sessionOwner(userStore.currentUser),
        token: userStore.accessToken || ''
    });
    const ownsSession = owner => !!owner?.token
        && owner.generation === generation
        && owner.owner === sessionOwner(userStore.currentUser)
        && !!userStore.accessToken;
    const ownsToken = owner => ownsSession(owner) && owner.token === userStore.accessToken;
    const canceled = config => new client.CanceledError('The request belongs to a previous session.', config);
    const isCanceled = (error, config) => client.isCancel(error)
        || config?.signal?.aborted || !!config?.cancelToken?.reason;
    const clearOwnedSession = (owner, options) => {
        if (ownsToken(owner)) clearSession(options);
    };

    const refreshAccessToken = owner => {
        if (!ownsToken(owner)) return Promise.reject(canceled());
        if (pendingRefresh?.owner.generation === owner.generation
            && pendingRefresh.owner.owner === owner.owner
            && pendingRefresh.owner.token === owner.token) return pendingRefresh.promise;

        const pending = { owner, promise: null };
        pendingRefresh = pending;
        pending.promise = Promise.resolve().then(() => {
            if (!ownsToken(owner)) throw canceled();
            return refreshSession();
        }).then(sessionData => {
            if (!ownsToken(owner)) throw canceled();
            if (!sessionData?.user || !sessionData?.token) throw new Error('Invalid refresh session response');
            if (sessionOwner(sessionData.user) !== owner.owner) throw canceled();

            refreshCommit = { user: sessionData.user, token: sessionData.token };
            try {
                userStore.setSession(refreshCommit);
            } finally {
                refreshCommit = null;
            }
            return sessionData;
        }).finally(() => {
            if (pendingRefresh === pending) pendingRefresh = null;
        });
        return pending.promise;
    };

    const requestInterceptor = client.interceptors.request.use(config => {
        // Axios dispatches retries asynchronously; the account can change before this runs.
        if (config._retry && !ownsSession(config._authSession)) throw canceled(config);
        if (!config.skipAuthHeader) {
            const owner = snapshot();
            config._authSession = owner;
            if (owner.token) {
                config.headers = config.headers || {};
                config.headers.Authorization = owner.token;
            }
        }
        return config;
    });

    const responseInterceptor = client.interceptors.response.use(response => response, async error => {
        const response = error?.response;
        const request = error?.config;
        const owner = request?._authSession;
        if (isCanceled(error, request) || response?.status !== 401 || request?.skipAuthRefresh
            || !ownsSession(owner)) throw error;

        const tokenExpired = response?.data?.data?.tokenExpired === 1;
        if (!tokenExpired || request._retry) {
            clearOwnedSession(owner, { notify: tokenExpired });
            throw error;
        }

        request._retry = true;
        try {
            // A concurrent refresh may already have replaced this request's expired token.
            if (ownsToken(owner)) await refreshAccessToken(owner);
            if (!ownsSession(owner)) throw canceled(request);
            if (isCanceled(null, request)) throw new client.CanceledError('Request canceled.', request);
            return client(request);
        } catch (refreshError) {
            if (!isCanceled(refreshError, request)) clearOwnedSession(owner, { notify: true });
            throw refreshError;
        }
    });

    return () => {
        unsubscribe();
        client.interceptors.request.eject(requestInterceptor);
        client.interceptors.response.eject(responseInterceptor);
    };
}
